import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, TextArea } from "../../components/common/Primitives";
import { Modal } from "../../components/common/Modal";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Sparkles, RefreshCw, Trash2, Edit3, Eye, ExternalLink, Plus } from "lucide-react";

export default function AdminBlogs() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [trending, setTrending] = useState({ sectors: [], suggested_topics: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blogs?limit=200");
      setPosts(r.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/blogs/topics/trending")
      .then((r) => setTrending(r.data || { sectors: [], suggested_topics: [] }))
      .catch(() => {});
  }, [load]);

  const togglePublish = async (slug, next) => {
    setBusy(true);
    try {
      await api.patch(`/admin/blogs/${slug}`, { published: next });
      toast.success(next ? "Published" : "Unpublished");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}" permanently?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/blogs/${slug}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell>
      <Toaster position="top-right" richColors />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-800">RESOURCE FEED</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-1">Blog & Resources</h1>
            <div className="text-ink-soft text-sm mt-1">
              AI-generated articles that power the public /resources page. Weekly cron refreshes trending sectors every Monday.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/resources" target="_blank" className="inline-flex items-center gap-1 text-sm text-emerald-800 hover:text-gold" data-testid="admin-blogs-public-link">
              <ExternalLink size={14} /> Public feed
            </Link>
            <Button
              variant="gold"
              onClick={() => setShowGen(true)}
              data-testid="admin-blogs-generate"
            >
              <Sparkles size={14} className="inline mr-1" /> Generate posts
            </Button>
          </div>
        </div>

        <Card>
          {loading ? (
            <div className="text-center py-10 text-ink-muted text-sm">Loading posts…</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-10">
              <div className="font-heading text-ink text-lg mb-1">No posts yet.</div>
              <div className="text-ink-soft text-sm mb-4">Generate your first article by clicking above.</div>
              <Button variant="gold" onClick={() => setShowGen(true)} data-testid="admin-blogs-empty-gen">
                <Plus size={14} className="inline mr-1" /> Generate first post
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {posts.map((p) => (
                <div key={p.slug} className="py-4 flex items-start justify-between gap-3" data-testid={`admin-blog-${p.slug}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Pill tone={p.published ? "success" : "muted"}>
                        {p.published ? "Published" : "Draft"}
                      </Pill>
                      {p.sector && <Pill tone="gold">{p.sector}</Pill>}
                      <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                        {p.generated_by || "?"} · {p.generated_at ? formatDateTime(p.generated_at) : "—"}
                      </span>
                    </div>
                    <div className="font-heading text-ink truncate">{p.title}</div>
                    <div className="text-[11px] font-mono text-ink-muted mt-0.5">/{p.slug}</div>
                    {p.excerpt && (
                      <p className="text-sm text-ink-soft mt-1 line-clamp-2">{p.excerpt}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0 flex-wrap">
                    <Link
                      to={`/resources/${p.slug}`}
                      target="_blank"
                      className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1"
                      data-testid={`admin-blog-preview-${p.slug}`}
                      title="Preview on public feed"
                    >
                      <Eye size={12} /> Preview
                    </Link>
                    <button
                      onClick={() => setEditing(p)}
                      className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-emerald-500 hover:text-emerald-800 inline-flex items-center gap-1"
                      data-testid={`admin-blog-edit-${p.slug}`}
                    >
                      <Edit3 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => togglePublish(p.slug, !p.published)}
                      disabled={busy}
                      className={`px-2 py-1 rounded border text-[11px] font-heading inline-flex items-center gap-1 ${
                        p.published
                          ? "border-navy/15 text-ink hover:border-rose-400 hover:text-rose-700"
                          : "border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                      }`}
                      data-testid={`admin-blog-publish-${p.slug}`}
                    >
                      {p.published ? "Unpublish" : "Publish"}
                    </button>
                    <button
                      onClick={() => remove(p.slug)}
                      disabled={busy}
                      className="px-2 py-1 rounded border border-rose-300 text-[11px] font-heading text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                      data-testid={`admin-blog-delete-${p.slug}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {showGen && (
        <GenerateModal
          trending={trending}
          onClose={() => setShowGen(false)}
          onDone={async () => { setShowGen(false); await load(); }}
        />
      )}
      {editing && (
        <EditModal
          post={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </DashboardShell>
  );
}

function GenerateModal({ trending, onClose, onDone }) {
  const [mode, setMode] = useState("trends"); // "trends" | "manual"
  const [topicText, setTopicText] = useState("");
  const [sector, setSector] = useState("");
  const [limit, setLimit] = useState(3);
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const body = mode === "trends"
        ? { from_trends: true, limit: Number(limit) || 3, publish }
        : {
            topics: topicText.split("\n").map((s) => s.trim()).filter(Boolean),
            sector: sector || null,
            publish,
          };
      if (mode === "manual" && (!body.topics || body.topics.length === 0)) {
        toast.info("Enter at least one topic (one per line).");
        setBusy(false);
        return;
      }
      const r = await api.post("/admin/blogs/generate", body);
      const jobId = r.data?.job_id;
      const target = r.data?.target_count || body.topics?.length || Number(limit) || 3;
      toast.info(`Drafting ${target} post(s) in the background — this can take 30-90s.`);
      // Poll until the job finishes or 3 minutes elapse.
      const started = Date.now();
      let last = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((res) => setTimeout(res, 4000));
        try {
          const j = await api.get(`/admin/blogs/jobs/${jobId}`);
          const produced = j.data?.produced_count || 0;
          if (produced > last) {
            toast.success(`Post ${produced}/${target} ready`);
            last = produced;
          }
          if (j.data?.status === "done") {
            toast.success(`Generated ${produced} post(s)`);
            break;
          }
          if (j.data?.status === "failed") {
            toast.error(j.data?.error || "Generation failed");
            break;
          }
        } catch (e) {
          toast.error(formatApiError(e));
          break;
        }
        if (Date.now() - started > 180000) {
          toast.info("Still working — refresh in a moment to see the latest posts.");
          break;
        }
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={true} title="Generate blog posts" onClose={onClose} size="lg">
      <div className="space-y-4 text-sm">
        <div className="flex gap-2" data-testid="gen-mode-toggle">
          <button
            onClick={() => setMode("trends")}
            className={`px-3 py-1.5 rounded text-xs font-heading border ${mode === "trends" ? "bg-emerald-600 text-white border-emerald-600" : "border-navy/15 text-ink hover:bg-navy/[0.04]"}`}
            data-testid="gen-mode-trends"
          >From trending sectors</button>
          <button
            onClick={() => setMode("manual")}
            className={`px-3 py-1.5 rounded text-xs font-heading border ${mode === "manual" ? "bg-emerald-600 text-white border-emerald-600" : "border-navy/15 text-ink hover:bg-navy/[0.04]"}`}
            data-testid="gen-mode-manual"
          >From my topics</button>
        </div>

        {mode === "trends" ? (
          <div className="space-y-3">
            <div className="text-xs text-ink-muted">
              Auto-picks the top trending sectors from live marketplace data and drafts one article per sector.
            </div>
            <div>
              <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Number of posts</label>
              <input
                type="number" min={1} max={6} value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="mt-1 w-24 px-3 py-2 rounded border border-navy/15 text-sm"
                data-testid="gen-limit"
              />
            </div>
            {trending.sectors && trending.sectors.length > 0 && (
              <div className="text-[11px] text-ink-muted">
                Top trending: <span className="font-mono">{trending.sectors.slice(0, 5).join(", ")}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Topics (one per line, up to 6)</label>
              <TextArea
                value={topicText}
                onChange={(e) => setTopicText(e.target.value)}
                rows={5}
                placeholder={"How Indian family offices approach 2026 allocations\nWhat MCA data reveals about founder credibility"}
                testId="gen-topics"
              />
            </div>
            <div>
              <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Sector focus (optional)</label>
              <input
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                placeholder="e.g. CleanTech"
                className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm"
                data-testid="gen-sector"
              />
            </div>
            {trending.suggested_topics && trending.suggested_topics.length > 0 && (
              <div className="text-[11px] text-ink-muted">
                Suggestions:{" "}
                {trending.suggested_topics.slice(0, 3).map((t, i) => (
                  <button
                    key={i}
                    onClick={() => setTopicText((prev) => (prev ? prev + "\n" : "") + t)}
                    className="underline hover:text-emerald-800 mr-2"
                    data-testid={`gen-suggest-${i}`}
                  >{t}</button>
                ))}
              </div>
            )}
          </div>
        )}

        <label className="inline-flex items-center gap-2 text-xs" data-testid="gen-publish-toggle">
          <input type="checkbox" checked={publish} onChange={(e) => setPublish(e.target.checked)} />
          Publish immediately (skip draft state)
        </label>

        <div className="flex justify-end gap-2 pt-4 border-t border-navy/10">
          <Button variant="outline" onClick={onClose} data-testid="gen-cancel">Cancel</Button>
          <Button variant="gold" onClick={run} disabled={busy} data-testid="gen-run">
            {busy ? <><RefreshCw size={14} className="inline mr-1 animate-spin" /> Generating…</> : <><Sparkles size={14} className="inline mr-1" /> Generate</>}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function EditModal({ post, onClose, onSaved }) {
  const [title, setTitle] = useState(post.title || "");
  const [excerpt, setExcerpt] = useState(post.excerpt || "");
  const [body, setBody] = useState(post.body_md || "");
  const [tag, setTag] = useState(post.tag || post.sector || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/blogs/${post.slug}`, {
        title, excerpt, body_md: body, tag,
      });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={true} title={`Edit · ${post.slug}`} onClose={onClose} size="xl">
      <div className="space-y-3 text-sm">
        <div>
          <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm"
            data-testid="edit-title" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Tag</label>
            <input value={tag} onChange={(e) => setTag(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm"
              data-testid="edit-tag" />
          </div>
        </div>
        <div>
          <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Excerpt</label>
          <TextArea rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)}
            testId="edit-excerpt" />
        </div>
        <div>
          <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Body (Markdown)</label>
          <TextArea rows={16} value={body} onChange={(e) => setBody(e.target.value)}
            testId="edit-body" />
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-navy/10">
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
          <Button variant="gold" onClick={save} disabled={busy} data-testid="edit-save">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
